import { useState, useEffect } from "react";
import { AxiosResponse } from "axios";
import { useParams } from "react-router-dom";
import { getEmployeeLeavesInfo, getEmployeeLeavesDetails, deleteHRLeave } from "api/apiHelper";
import { EmployeeLeaveInfoType, EmployeeLeavesDetailsType } from "types/leaves-types";
import LeavesInfo from "features/leaves/LeavesInfo";
import LeavesStatusTable from "features/leaves/LeavesStatusTable";
import ErrorBox from "components/ErrorBox";
import Backdrop from "components/Backdrop";
import RequestLeaveForm from "features/leaves/RequestLeaveForm";
import { usePermissions } from "app/hooks";
import Button from "components/Button";
import { toast } from "react-toastify";

const EmployeeLeavesSection = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [leaveInfo, setLeaveInfo] = useState<EmployeeLeaveInfoType[]>([]);
  const [leavesDetail, setLeavesDetail] = useState<EmployeeLeavesDetailsType[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showRequestLeave, setShowRequestLeave] = useState(false);
  const [showAddLeaveForm, setShowAddLeaveForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<EmployeeLeavesDetailsType | null>(null);

  const permissions = usePermissions() || [];

  const fetchLeaves = async (id: string) => {
    setIsLoading(true);
    setError("");

    try {
      if (permissions.includes("leaves.view_employeeleaveinfo")) {
        const response: AxiosResponse<EmployeeLeaveInfoType[]> = await getEmployeeLeavesInfo(id);
        setLeaveInfo(response.data);
        console.log("leaves info:", response.data);
      }

      if (permissions.includes("leaves.view_employeeleavesdetails")) {
        const response: AxiosResponse<EmployeeLeavesDetailsType[]> = await getEmployeeLeavesDetails(id);
        setLeavesDetail(response.data);
        console.log("leaves detail:", response.data);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching leaves data.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteLeave = async () => {
    if (!selectedLeave) return;
    try {
      await deleteHRLeave({ leave_id: selectedLeave.id });
      toast.success("Leave deleted successfully");
      setShowDeleteModal(false);
      if (employeeId) fetchLeaves(employeeId);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.error || "Failed to delete leave");
    }
  };
  const handleRequestDelete = (leave: EmployeeLeavesDetailsType) => {
    setSelectedLeave(leave);
    setShowDeleteModal(true);
  }
  const handleCloseRequestLeave = () => {
    setShowRequestLeave(false);
    if (employeeId) fetchLeaves(employeeId);
  };

  useEffect(() => {
    if (employeeId) fetchLeaves(employeeId);
  }, [employeeId]);

  if (isLoading) return <Backdrop show={true} />;

  if (showRequestLeave) {
    return <RequestLeaveForm setShowRequestLeave={handleCloseRequestLeave} isHR={permissions.includes("leaves.view_employeeleavesdetails")}
    selectedEmployeeId={employeeId}/>;
  }

  return (
    <div className="mt-5 flex flex-col gap-4">
      {error && <ErrorBox errorMessage={error} />}
      
      {/* HR Add Leave Button & Form */}
      {permissions.includes("leaves.add_employeeleavesdetails") && (
        <>
          {!showAddLeaveForm ? (
            <div className="flex justify-end">
              <Button
                varient="primary"
                onClick={() => setShowAddLeaveForm(true)}
                className="px-4 py-1"
              >
                <i className="fas fa-plus text-[0.9rem] me-2"></i>
                <span>Add Employee Leave</span>
              </Button>
            </div>
          ) : (
            <div className="mb-4">
              <RequestLeaveForm 
                isHR={true}
                selectedEmployeeId={employeeId}
                setShowRequestLeave={setShowAddLeaveForm}
              />
            </div>
          )}
        </>
      )}


      {permissions.includes("leaves.view_employeeleaveinfo") && (
        <LeavesInfo leaveData={leaveInfo} />
      )}

      {permissions.includes("leaves.view_employeeleavesdetails") && (
        <LeavesStatusTable tableData={leavesDetail} tableHeading="Leaves Details" onRequestDelete={handleRequestDelete}  showDelete={true}/>
      )}
      {showDeleteModal && selectedLeave && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl w-full max-w-md transform transition-all">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <i className="fas fa-exclamation-triangle text-3xl text-red-600 dark:text-red-400"></i>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 text-center">
              Confirm Delete
            </h2>

            {/* Message */}
            <p className="text-gray-600 dark:text-gray-300 text-center mb-6 leading-relaxed">
              Are you sure you want to delete leave <span className="font-semibold text-gray-900 dark:text-white">#{selectedLeave.id}</span>?
              <br />
              <span className="text-sm">({selectedLeave.leave_type_description})</span>
            </p>

            {/* Warning box */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-6">
              <p className="text-sm text-red-800 dark:text-red-300 text-center">
                <i className="fas fa-info-circle mr-2"></i>
                This action cannot be undone
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                varient="secondary"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2.5 text-base font-medium"
              >
                Cancel
              </Button>
              <Button
                varient="danger"
                onClick={handleDeleteLeave}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-base font-medium"
              >
                <i className="fas fa-trash-alt mr-2"></i> Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeLeavesSection;
